import React, { type FC, type ReactNode, useCallback, useContext, useRef } from 'react';
import type { ITooltipProps } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { generateUuidV4 } from 'reactApp/utils/helpers';

import { tooltipPlacements } from './Tooltip.constants';
import { TooltipContext } from './Tooltip.context';
import type { ID } from './Tooltip.types';

type ChildrenWithExtraProps<P> = P & { children?: ReactNode | FC<{ onClose: () => void }> };

interface UseTooltipProps {
    /** Идентификатор конкретного вида тултипов(если задан, то одновременно будет отображаться один тултип с данным ID) */
    id?: ID;
    /** Пропсы компонента */
    defaultProps: Partial<ChildrenWithExtraProps<ITooltipProps>>;
}

export const useTooltip = ({ id: controlledId, defaultProps = {} }: UseTooltipProps) => {
    const tooltipContext = useContext(TooltipContext);

    const uid = useRef(generateUuidV4());

    const handleClose = useCallback(() => {
        tooltipContext.close(uid.current);
    }, [tooltipContext]);

    const handleOpen = useCallback(() => {
        let children = defaultProps.children;

        if (!React.isValidElement(defaultProps.children) && typeof children === 'function') {
            /** Компонент Tooltip хочет children как ReactNode */
            children = children({
                onClose: handleClose,
            });
        }

        const initialProps = {
            children,
            placement: tooltipPlacements.BOTTOM,
            onClose: handleClose,
            target: null,
            smartPlacement: true,
        };

        const fullProps = Object.assign(initialProps, defaultProps, {
            children,
        });

        tooltipContext.open(uid.current, fullProps, controlledId);
    }, [controlledId, defaultProps, handleClose, tooltipContext]);

    return {
        open: handleOpen,
        close: handleClose,
    };
};
